import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Chip, Alert,
  CircularProgress, IconButton, Tooltip, Grid, Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CheckIcon from '@mui/icons-material/Check';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DescriptionIcon from '@mui/icons-material/Description';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { reminderService } from '../../services/financeService';
import './Financial.css';

const TYPE_META = {
  CHECK: {
    label: 'Check',
    icon: <AccountBalanceIcon fontSize="small" />,
    color: 'var(--color-accent-hover)',
    bg: 'var(--color-accent-light)',
  },
  INVOICE: {
    label: 'Invoice',
    icon: <ReceiptIcon fontSize="small" />,
    color: 'var(--color-info)',
    bg: 'var(--color-info-light)',
  },
  PURCHASE: {
    label: 'Vendor Bill',
    icon: <ShoppingCartIcon fontSize="small" />,
    color: 'var(--color-warning)',
    bg: 'var(--color-warning-light)',
  },
  GD_DEADLINE: {
    label: 'GD Deadline',
    icon: <DescriptionIcon fontSize="small" />,
    color: 'var(--color-warning)',
    bg: 'var(--color-warning-light)',
  },
};

const SEVERITY_META = {
  OVERDUE: { label: 'Overdue', color: 'error', icon: <ErrorIcon fontSize="small" />, border: 'var(--color-error-border)', edge: 'var(--color-error)', rowBg: 'var(--color-error-light)' },
  DUE_TODAY: { label: 'Due Today', color: 'error', icon: <NotificationsActiveIcon fontSize="small" />, border: 'var(--color-error-border)', edge: 'var(--color-error)', rowBg: 'var(--color-error-light)' },
  DUE_SOON: { label: 'Due in 1-3 days', color: 'warning', icon: <WarningAmberIcon fontSize="small" />, border: 'var(--color-warning-border)', edge: 'var(--color-warning)', rowBg: 'var(--color-warning-light)' },
  UPCOMING: { label: 'Upcoming', color: 'info', icon: <CalendarTodayIcon fontSize="small" />, border: 'var(--color-info-border)', edge: 'var(--color-info)', rowBg: 'var(--color-info-light)' },
};

const fmtAmount = (amount, currency) => {
  if (!amount) return '—';
  return `${currency || 'PKR'} ${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
};

const fmtDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const daysLabel = (days) => {
  if (days < 0) return `${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''} overdue`;
  if (days === 0) return 'Due today';
  return `Due in ${days} day${days !== 1 ? 's' : ''}`;
};

// ─── Summary Card ──────────────────────────────────────────────────────────
const SummaryBox = ({ count, label, color, icon }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 1.5, sm: 2 },
      border: '1px solid',
      borderColor: color === 'error' ? 'var(--color-error-border)' : color === 'warning' ? 'var(--color-warning-border)' : color === 'info' ? 'var(--color-info-border)' : 'var(--color-border-medium)',
      borderRadius: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
    }}
  >
    <Box sx={{ width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: color === 'error' ? 'var(--color-error-light)' : color === 'warning' ? 'var(--color-warning-light)' : color === 'info' ? 'var(--color-info-light)' : 'var(--color-bg-tertiary)',
        flexShrink: 0 }}>
      {React.cloneElement(icon, { sx: { color: color === 'error' ? 'var(--color-error)' : color === 'warning' ? 'var(--color-warning)' : color === 'info' ? 'var(--color-primary)' : 'var(--color-neutral-700)' } })}
    </Box>
    <Box>
      <Typography variant="h5" fontWeight={700} lineHeight={1}>{count}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>{label}</Typography>
    </Box>
  </Paper>
);

// ─── Reminder Card ─────────────────────────────────────────────────────────
const ReminderCard = ({ item, onNavigate }) => {
  const sev = SEVERITY_META[item.severity] || SEVERITY_META.UPCOMING;
  const typ = TYPE_META[item.type] || TYPE_META.INVOICE;

  return (
    <Paper
      elevation={0}
      onClick={() => onNavigate(item.navigationPath)}
      sx={{
        border: '1px solid',
        borderColor: 'var(--color-border-medium)',
        borderLeft: `3px solid ${sev.edge}`,
        borderRadius: 2,
        p: { xs: 1.5, sm: 2 },
        mb: 1.5,
        bgcolor: 'var(--color-bg-primary)',
        transition: 'border-color 120ms, box-shadow 120ms',
        '&:hover': { borderColor: 'var(--color-border-strong)', boxShadow: 'var(--shadow-sm)' },
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      {/* Type icon */}
      <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: typ.bg, display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: typ.color }}>
        {typ.icon}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.25 }}>
          <Typography variant="body2" fontWeight={700} noWrap sx={{ flex: 1 }}>
            {item.title}
          </Typography>
          <Chip
            label={daysLabel(item.daysUntilDue)}
            color={sev.color}
            size="small"
            sx={{ fontSize: '0.68rem', height: 20, flexShrink: 0 }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
          {item.description}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
          <Chip label={typ.label} size="small" sx={{ fontSize: '0.65rem', height: 18, bgcolor: typ.bg, color: typ.color, border: `1px solid color-mix(in srgb, ${typ.color} 22%, transparent)` }} />
          <Chip label={`Due: ${fmtDate(item.dueDate)}`} size="small" sx={{ fontSize: '0.65rem', height: 18 }} variant="outlined" />
          <Chip label={fmtAmount(item.amount, item.currency)} size="small" sx={{ fontSize: '0.65rem', height: 18 }} variant="outlined" />
        </Box>
      </Box>

      {/* Arrow */}
      <ArrowForwardIosIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
    </Paper>
  );
};

// ─── Grouped Section ───────────────────────────────────────────────────────
const ReminderGroup = ({ title, items, color, onNavigate }) => {
  if (!items.length) return null;
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={700} color={`${color}.main`} sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.08em' }}>
          {title}
        </Typography>
        <Chip label={items.length} color={color} size="small" sx={{ fontSize: '0.7rem', height: 18 }} />
      </Box>
      {items.map((item, i) => <ReminderCard key={i} item={item} onNavigate={onNavigate} />)}
    </Box>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────
const RemindersPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchReminders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await reminderService.getAll();
      setData(res.data || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReminders(); }, [fetchReminders]);

  const overdue   = data?.items?.filter(i => i.severity === 'OVERDUE') || [];
  const dueToday  = data?.items?.filter(i => i.severity === 'DUE_TODAY') || [];
  const dueSoon   = data?.items?.filter(i => i.severity === 'DUE_SOON') || [];
  const upcoming  = data?.items?.filter(i => i.severity === 'UPCOMING') || [];
  const total     = data?.totalCount || 0;

  return (
    <Box className="financial-container">
      {/* Header */}
      <Box className="page-header">
        <Box>
          <Typography variant="h5" fontWeight={700}>Reminders & Alerts</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Upcoming and overdue checks, invoices, vendor bills, and GD settlement deadlines (next 14 days)
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={fetchReminders} disabled={loading}><RefreshIcon /></IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Summary Cards */}
      {data && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <SummaryBox count={data.overdueCount} label="Overdue" color="error" icon={<ErrorIcon />} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryBox count={data.dueTodayCount} label="Due Today" color="error" icon={<NotificationsActiveIcon />} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryBox count={data.dueSoonCount} label="Due in 1-3 Days" color="warning" icon={<WarningAmberIcon />} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryBox count={data.upcomingCount} label="Upcoming (4-14d)" color="info" icon={<CalendarTodayIcon />} />
          </Grid>
        </Grid>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty */}
      {!loading && total === 0 && (
        <Paper elevation={0} sx={{ p: 5, textAlign: 'center', border: '1px dashed var(--color-border-strong)', borderRadius: 2 }}>
          <CheckIcon sx={{ fontSize: 56, color: 'success.light', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">All clear!</Typography>
          <Typography variant="body2" color="text.secondary">No upcoming or overdue items in the next 14 days.</Typography>
        </Paper>
      )}

      {/* Groups */}
      {!loading && total > 0 && (
        <>
          <ReminderGroup title="Overdue" items={overdue} color="error" onNavigate={navigate} />
          <ReminderGroup title="Due Today" items={dueToday} color="error" onNavigate={navigate} />
          <ReminderGroup title="Due in 1-3 Days" items={dueSoon} color="warning" onNavigate={navigate} />
          <ReminderGroup title="Upcoming (4-14 Days)" items={upcoming} color="info" onNavigate={navigate} />

          <Divider sx={{ my: 2 }} />
          <Alert severity="info" sx={{ mt: 1 }}>
            <strong>Tip:</strong> Click any item to go directly to that module. Checks → Payments page · Invoices → Invoices page · Vendor Bills → Purchases page · GD → GD & Exports page.
          </Alert>
        </>
      )}
    </Box>
  );
};

export default RemindersPage;
